import Menu, { MenuItem } from '@veeva/corgix/menu';
import { ThemeProvider } from 'emotion-theming';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import MenuDivider from '../../common/components/MenuDivider';
import SearchResultLink from './SearchResultLink';
import {
    searchResultLinkOverrideCSS,
    searchResultsBrowserAllAppFamiliesCSS,
    searchResultsNoResultsFoundCSS,
} from './styles/SearchResultsMenu.styles';
import addUserPreferenceRecentlySearchedItem from '../services/addUserPreferenceRecentlySearchedItem';
import PageGroupType, { getPageGroupTypeFromUri, PageGroupUri } from './PageGroupType';
import RecentlySearchedSearchResultLink from './RecentlySearchedSearchResultLink';
import getUpdateRecentlySearchedList from '../services/getUpdateRecentlySearchedList';
import AppContext from '../../common/containers/AppContext';

export default function SearchResultsMenu({
    className,
    customers = [],
    families = [],
    platforms = [],
    focusedValue,
    onMenuItemClick,
    onMenuMapUpdate,
    searchText,
}) {
    const searchWords = searchText?.split(' ') || [];
    const allFamiliesUrl = `/${PageGroupUri.FAMILY}`;
    const { recentlySearchedIdsByType, setRecentlySearchedIdsByType } = useContext(AppContext);
    const handleMenuItemClick = (_, keyValue) => {
        if (keyValue !== 'not-found') {
            const [, queryType, queryId] = keyValue.split('/');
            const pageGroupType = getPageGroupTypeFromUri(queryType);
            if (pageGroupType !== undefined && queryId) {
                addUserPreferenceRecentlySearchedItem(pageGroupType, queryId);
                setRecentlySearchedIdsByType((prevData) => ({
                    ...prevData,
                    [pageGroupType]: getUpdateRecentlySearchedList(
                        recentlySearchedIdsByType[pageGroupType],
                        Number(queryId),
                    ),
                }));
            }

            // Only close search results if a real item was clicked (not-found is not a real item)
            onMenuItemClick();
        }
    };

    return (
        // Set padding on the link instead of the MenuItem so the entire MenuItem is a clickable link
        <ThemeProvider theme={{ menuSpacing: 0, menuSpacingVariant1: 0 }}>
            <Menu
                className={className}
                /* UIP-7475 if menu item length doesn't change but the content changes, the menu map won't be updated and keyboard nav stops working */
                key={searchText}
                focusedKeyValue={focusedValue}
                onClick={handleMenuItemClick}
                onMenuMapUpdate={onMenuMapUpdate}
                selectedValue={focusedValue}
            >
                {platforms.map((p) => {
                    const url = `/${PageGroupUri.PLATFORM}/${p.id}`;
                    return p.isRecentlySearched ? (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <RecentlySearchedSearchResultLink
                                category={PageGroupType.PLATFORM}
                                css={searchResultLinkOverrideCSS}
                                label={p.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    ) : (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <SearchResultLink
                                category={PageGroupType.PLATFORM}
                                css={searchResultLinkOverrideCSS}
                                label={p.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    );
                })}

                {platforms.length && families.length ? <MenuDivider /> : null}
                {families.map((f) => {
                    const url = `/${PageGroupUri.FAMILY}/${f.id}`;
                    return f.isRecentlySearched ? (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <RecentlySearchedSearchResultLink
                                category={PageGroupType.FAMILY}
                                css={searchResultLinkOverrideCSS}
                                label={f.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    ) : (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <SearchResultLink
                                category={PageGroupType.FAMILY}
                                css={searchResultLinkOverrideCSS}
                                label={f.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    );
                })}
                {families?.length > 0 ? (
                    <MenuItem key={allFamiliesUrl} value={allFamiliesUrl} keyValue={allFamiliesUrl}>
                        <Link css={searchResultsBrowserAllAppFamiliesCSS} to={allFamiliesUrl}>
                            Browse All App Families
                        </Link>
                    </MenuItem>
                ) : null}

                {(customers.length && families.length) || (customers.length && platforms.length) ? (
                    <MenuDivider />
                ) : null}
                {customers.map((c) => {
                    const url = `/${PageGroupUri.CUSTOMER}/${c.id}`;
                    return c.isRecentlySearched ? (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <RecentlySearchedSearchResultLink
                                category={PageGroupType.CUSTOMER}
                                css={searchResultLinkOverrideCSS}
                                label={c.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    ) : (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <SearchResultLink
                                category={PageGroupType.CUSTOMER}
                                css={searchResultLinkOverrideCSS}
                                label={c.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    );
                })}
                {!customers.length && !families.length && !platforms.length ? (
                    <MenuItem
                        css={searchResultsNoResultsFoundCSS}
                        value="not-found"
                        keyValue="not-found"
                    >
                        No results found
                    </MenuItem>
                ) : null}
            </Menu>
        </ThemeProvider>
    );
}

SearchResultsMenu.propTypes = {
    className: PropTypes.string,
    customers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            label: PropTypes.string,
        }),
    ),
    families: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            label: PropTypes.string,
        }),
    ),
    focusedValue: PropTypes.string,
    onMenuItemClick: PropTypes.func,
    onMenuMapUpdate: PropTypes.func,
    platforms: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            label: PropTypes.string,
        }),
    ),
    searchText: PropTypes.string,
};
